/**
 * recommendationSynthesizer.ts
 * 
 * Centralized, context-grounded AI Coach Recommendation Engine for Trainova AI (Frontend).
 * Generates dynamic, highly relevant coaching feedback for ANY scenario, persona,
 * and competency category without hardcoding scenario-specific keywords.
 * 
 * Rules enforced:
 * 1. Strict cross-domain and scenario isolation (no banking in retail, no retail delivery in B2B SaaS/software, no IT troubleshoot in sales).
 * 2. Elimination of generic boilerplate placeholders and repetitive clichés.
 * 3. Requirement 8: "Try saying:" sentence displayed FIRST, followed by coach advice.
 * 4. Grounded in actual customer prompts, learner responses, and evaluator feedback.
 */

const GENERIC_PATTERNS = [
  /improve spoken communication/i,
  /in your next attempt,?\s*articulate clear and complete statements demonstrating/i,
  /in your next attempt,?\s*actively articulate statements covering/i,
  /demonstrate .* clearly in your spoken responses to meet scenario expectations/i,
  /focus on mastering .* during the active conversation stage/i,
  /demonstrate active listening and structured communication/i,
  /maintain clear articulation and verify resolution before closing/i,
  /acknowledge the customer prompt directly and outline clear next steps with empathy/i,
  /practice standard de-escalation response/i,
  /in your next turn,?\s*actively demonstrate/i,
  /here is the recommended response/i,
  /structure your response with clear, reassuring language that directly answers/i,
  /by verifying the record and confirming the solution/i,
  /let me take care of this for you immediately by verifying the record/i,
  /let me take personal ownership and outline the concrete steps/i,
  /i understand your concern\. let me take care of this for you immediately/i,
  /core competence development priority/i,
  /in your next attempt,?\s*address .* explicitly\. try saying/i,
  /timestamp unavailable in development mode/i,
  /your response/i,
  /focus on mastering/i,
  /confirm the final delivery/i,
  /walk with you over to the counter/i,
  /guide you through the checkout counter/i,
  /finalize the paperwork right now and ensure you receive immediate confirmation and tracking details/i,
  /here is our direct resolution plan: first, we will lock in the details right now, and second, I will confirm the final delivery/i,
  /I will have all details confirmed for you immediately, and you will receive a complete summary and schedule right after this step/i
];

export function isGenericRecommendation(text?: string | null): boolean {
  if (!text || typeof text !== 'string') return true;
  const trimmed = text.trim();
  if (trimmed.length < 15) return true;
  for (const pattern of GENERIC_PATTERNS) {
    if (pattern.test(trimmed)) return true;
  }
  return false;
}

export function cleanSpoken(text?: string | null): string {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/^(CUSTOMER|LEARNER|AI|USER|PERSONA)\s*(\([^)]*\))?\s*(SAID|SAYS)?\s*:\s*/i, '')
    .replace(/^\[(CUSTOMER|LEARNER|AI|USER|PERSONA|[^\]]+)\]\s*:\s*/i, '')
    .replace(/^["']|["']$/g, '')
    .trim();
}

export function normalizeCategory(competencyName?: string | null, category?: string | null, criterionId?: string | null): string {
  const combined = `${competencyName || ''} ${category || ''} ${criterionId || ''}`.toLowerCase();
  
  if (combined.includes('discov') || combined.includes('need') || combined.includes('inquiry') || combined.includes('enquiry') || combined.includes('requirement') || combined.includes('questioning') || combined.includes('probing') || combined.includes('qualification')) {
    return 'DISCOVERY';
  }
  // Value Alignment / Solution Presentation in sales/consulting/SaaS (MUST come before generic resolution/troubleshooting)
  if (combined.includes('value_alignment') || combined.includes('value alignment') || combined.includes('solution & value') || combined.includes('solution and value') || combined.includes('value prop') || combined.includes('roi') || combined.includes('business impact') || combined.includes('solution fit') || combined.includes('pitch') || combined.includes('consultative') || (combined.includes('value') && combined.includes('align'))) {
    return 'VALUE_ALIGNMENT';
  }
  if (combined.includes('product') || combined.includes('spec') || combined.includes('feature') || combined.includes('benefit') || combined.includes('solution knowledge') || combined.includes('commercial') || combined.includes('clarity')) {
    return 'PRODUCT_KNOWLEDGE';
  }
  if (combined.includes('object') || combined.includes('compet') || combined.includes('hesitat') || combined.includes('price resist') || combined.includes('pushback') || combined.includes('pricing') || combined.includes('budget') || combined.includes('resistance')) {
    return 'OBJECTION_HANDLING';
  }
  if (combined.includes('close') || combined.includes('purchase') || combined.includes('decision') || combined.includes('checkout') || combined.includes('payment step') || combined.includes('finalize') || combined.includes('next step') || combined.includes('next_step') || combined.includes('demo') || combined.includes('trial')) {
    return 'CLOSING';
  }
  if (combined.includes('owner') || combined.includes('accountab') || combined.includes('follow_up') || combined.includes('follow up') || combined.includes('commitment')) {
    return 'OWNERSHIP';
  }
  if (combined.includes('empath') || combined.includes('validat') || combined.includes('urgency') || combined.includes('de-escalat') || combined.includes('reassurance')) {
    return 'EMPATHY';
  }
  if (combined.includes('secur') || combined.includes('privac') || combined.includes('complian') || combined.includes('fraud') || combined.includes('confidential') || combined.includes('auth') || combined.includes('identity')) {
    return 'SECURITY';
  }
  if (combined.includes('accura') || combined.includes('transpar') || combined.includes('clarity') || combined.includes('fact') || combined.includes('realistic')) {
    return 'ACCURACY';
  }
  // Strict resolution (technical troubleshooting / defect / ticket / incident resolution ONLY, NOT consultative solution pitch)
  if (combined.includes('issue resolut') || combined.includes('problem resolut') || combined.includes('complaint resolut') || combined.includes('troubleshoot') || combined.includes('incident') || combined.includes('ticket') || combined.includes('bug') || combined.includes('defect')) {
    return 'RESOLUTION';
  }
  if (combined.includes('listen') || combined.includes('customer experience') || combined.includes('rapport') || combined.includes('communicat') || combined.includes('spoken') || combined.includes('service')) {
    return 'COMMUNICATION';
  }
  return 'GENERAL';
}

export function isCrossDomainContaminated(text?: string | null, scenarioContext: { title?: string | null; category?: string | null; description?: string | null } = {}): boolean {
  if (!text || typeof text !== 'string') return false;
  const lower = text.toLowerCase();
  const contextStr = `${scenarioContext.title || ''} ${scenarioContext.category || ''} ${scenarioContext.description || ''}`.toLowerCase();

  const isBankingScenario = /\b(bank|banking|fintech|loan|kyc|branch|deposit|credit card|emi|fd|account|savings|interest rate)\b/i.test(contextStr);
  const isSoftwareSaaSScenario = /\b(saas|software|crm|platform|subscription|cloud|app|portal|b2b|digital|lead|pipeline|enquir)\b/i.test(contextStr);
  const isRetailApplianceScenario = /\b(retail|appliance|fridge|refrigerator|electrolux|freezer|store|shop|merchandise)\b/i.test(contextStr) && !isSoftwareSaaSScenario;
  const isTechSupportScenario = /\b(it helpdesk|tech support|ticket|password reset|network|vpn|server|login error)\b/i.test(contextStr);
  const isSalesScenario = /\b(sales|buyer|selling|prospect|deal|lead|quota|negotiation|b2b)\b/i.test(contextStr);

  // Physical retail shipping / goods delivery keywords
  const hasPhysicalRetailOrDelivery = /\b(confirm the final delivery|final delivery|schedule delivery|order and delivery|walk with you over to the counter|to the counter|checkout counter|over to the counter|warehouse|freight|cartons|fridge|refrigerator|convertible freezer|electrolux)\b/i.test(lower);

  // If scenario is Software/SaaS, Banking, Tech Support, or HR, reject physical retail/delivery jargon
  if ((isSoftwareSaaSScenario || isBankingScenario || isTechSupportScenario) && hasPhysicalRetailOrDelivery) {
    return true;
  }

  // If a Sales/Negotiation scenario receives support-troubleshooting or incident-resolution phrases
  if (isSalesScenario && !isTechSupportScenario) {
    if (/\b(direct resolution plan|troubleshoot your ticket|escalate your ticket|network diagnostic|hardware replacement)\b/i.test(lower)) {
      return true;
    }
  }

  // If not banking, reject banking-specific jargon
  if (!isBankingScenario) {
    if (/\b(rent due|rent payment|rent issue|rent deadline|salary account|debit hold|debit restriction|kyc notice|aadhar|aadhaar|unauthorized card charges|fixed deposit|fd renewal)\b/i.test(lower)) {
      return true;
    }
  }

  // If not appliance retail, reject refrigerator specific jargon
  if (!isRetailApplianceScenario) {
    if (/\b(convertible freezer|refrigerator|fridge freezer|electrolux)\b/i.test(lower)) {
      return true;
    }
  }

  // If not software/SaaS, reject CRM/SaaS subscription jargon
  if (!isSoftwareSaaSScenario) {
    if (/\b(crm subscription|user per month|lead tracking|software license|b2b saas)\b/i.test(lower)) {
      return true;
    }
  }

  return false;
}

export function formatTrySayingFirst(trySaying?: string | null, advice?: string | null): string {
  const cleanScript = (trySaying || '').replace(/^[:\s"']+|["']\s*$/g, '').trim();
  const cleanAdvice = (advice || '').trim();

  if (cleanScript && cleanAdvice) {
    return `Try saying: "${cleanScript}" ${cleanAdvice}`;
  }
  if (cleanScript) {
    return `Try saying: "${cleanScript}"`;
  }
  return cleanAdvice;
}

export function parseAndFormatRecommendation(text?: string | null): { advice: string; trySaying: string; fullRecommendation: string } {
  if (!text || typeof text !== 'string') return { advice: '', trySaying: '', fullRecommendation: '' };
  const trimmed = text.trim();

  // If text starts with "Try saying:"
  if (/^Try saying:\s*/i.test(trimmed)) {
    const afterPrefix = trimmed.replace(/^Try saying:\s*/i, '').trim();
    let script = afterPrefix;
    let advice = '';

    const firstChar = afterPrefix[0];
    if (firstChar === '"' || firstChar === "'") {
      const closingIndex = afterPrefix.lastIndexOf(firstChar);
      if (closingIndex > 0) {
        script = afterPrefix.substring(1, closingIndex).trim();
        advice = afterPrefix.substring(closingIndex + 1).trim();
      } else {
        script = afterPrefix.replace(/^["']|["']$/g, '').trim();
      }
    } else {
      script = afterPrefix.replace(/^["']|["']$/g, '').trim();
    }

    return {
      advice,
      trySaying: script,
      fullRecommendation: formatTrySayingFirst(script, advice)
    };
  }

  // If text contains "Try saying:" later in the string
  if (/Try saying:\s*/i.test(trimmed)) {
    const parts = trimmed.split(/Try saying:\s*/i);
    const advice = (parts[0] || '').trim();
    let trySaying = (parts[1] || '').trim();
    
    const firstChar = trySaying[0];
    if (firstChar === '"' || firstChar === "'") {
      const closingIndex = trySaying.lastIndexOf(firstChar);
      if (closingIndex > 0) {
        trySaying = trySaying.substring(1, closingIndex).trim();
      } else {
        trySaying = trySaying.replace(/^["']|["']$/g, '').trim();
      }
    } else {
      trySaying = trySaying.replace(/^[:\s"']+|["']\s*$/g, '').trim();
    }

    return {
      advice,
      trySaying,
      fullRecommendation: formatTrySayingFirst(trySaying, advice)
    };
  }

  return {
    advice: trimmed,
    trySaying: '',
    fullRecommendation: trimmed
  };
}

export interface PersonalizedRecommendationInput {
  type?: string | null;
  competencyName?: string | null;
  category?: string | null;
  criterionId?: string | null;
  customerSaid?: string | null;
  userSaid?: string | null;
  whyItMatters?: string | null;
  explanation?: string | null;
  existingSuggestion?: string | null;
  scenarioTitle?: string | null;
  scenarioDescription?: string | null;
}

export interface PersonalizedRecommendationResult {
  advice: string;
  trySaying: string;
  fullRecommendation: string;
}

export function synthesizePersonalizedRecommendation({
  type = 'IMPROVEMENT',
  competencyName = 'Communication',
  category = '',
  criterionId = '',
  customerSaid = '',
  userSaid = '',
  whyItMatters = '',
  explanation = '',
  existingSuggestion = '',
  scenarioTitle = '',
  scenarioDescription = ''
}: PersonalizedRecommendationInput): PersonalizedRecommendationResult {
  const isStrength = (type || '').toUpperCase() === 'STRENGTH';
  const cleanCustomer = cleanSpoken(customerSaid);
  const cleanUser = cleanSpoken(userSaid);
  const compCat = normalizeCategory(competencyName, category, criterionId);
  const scenarioCtx = { title: scenarioTitle, category, description: scenarioDescription };

  // If existing suggestion is valid, non-generic, and free of cross-domain contamination, preserve its content while reordering "Try saying:" first
  if (
    existingSuggestion &&
    !isGenericRecommendation(existingSuggestion) &&
    !isCrossDomainContaminated(existingSuggestion, scenarioCtx) &&
    /Try saying:\s*/i.test(existingSuggestion)
  ) {
    const parsed = parseAndFormatRecommendation(existingSuggestion);
    if (parsed.trySaying && !isGenericRecommendation(parsed.trySaying) && !isCrossDomainContaminated(parsed.trySaying, scenarioCtx)) {
      return parsed;
    }
  }

  const custLower = cleanCustomer.toLowerCase();
  const userLower = cleanUser.toLowerCase();
  const titleLower = (scenarioTitle || '').toLowerCase();
  const combinedContext = `${titleLower} ${category || ''} ${scenarioDescription || ''}`.toLowerCase();

  // Extract scenario domain cues dynamically without rigid hardcoding
  const isSaaS = /\b(saas|software|crm|platform|subscription|cloud|app|portal|b2b|lead|pipeline|enquir)\b/i.test(combinedContext);
  const isBanking = /\b(bank|banking|fintech|loan|kyc|branch|deposit|credit card|emi|fd|account|savings|interest rate)\b/i.test(combinedContext);
  const isRetail = /\b(retail|store|shop|appliance|fridge|refrigerator|electrolux|freezer|merchandise)\b/i.test(combinedContext) && !isSaaS;
  const isTech = /\b(support|helpdesk|it|ticket|bug|network|system|access|login|error|vpn|server)\b/i.test(combinedContext);

  const evaluatorNote = (whyItMatters || explanation || '').trim();

  let advice = '';
  let trySaying = '';

  if (isStrength) {
    // ---------------- STRENGTH REINFORCEMENT ----------------
    switch (compCat) {
      case 'VALUE_ALIGNMENT':
        if (isSaaS) {
          advice = "You connected the CRM capabilities to the customer's stated challenges. To strengthen the value proposition, pair the pricing breakdown with a concrete ROI comparison or workflow efficiency gain.";
          if (custLower.includes('value') || custLower.includes('cost') || custLower.includes('pricing') || custLower.includes('three') || custLower.includes('3')) {
            trySaying = "For your 3-person team, preventing even one or two missed client follow-ups each month easily covers the subscription cost while recovering hours lost to manual WhatsApp coordination.";
          } else {
            trySaying = "Along with centralizing your customer records, automated reminders ensure your team never drops a warm lead, turning missed opportunities into measurable revenue.";
          }
        } else if (isBanking) {
          advice = "You aligned the financial product benefits to the customer's wealth goals. Reinforce this by highlighting guaranteed returns and principal safety.";
          trySaying = "This account structure ensures your principal remains 100% secure while earning higher guaranteed interest to meet your financial goals.";
        } else if (isRetail) {
          advice = "You connected product specifications to the customer's lifestyle needs. Reinforce this by summarizing daily convenience and energy savings.";
          trySaying = "This model directly matches your space requirements while cutting daily power costs through high-efficiency performance.";
        } else {
          advice = "You articulated the core value proposition effectively. Continue linking each feature directly to solving the customer's stated needs.";
          trySaying = "Along with these core capabilities, this solution directly addresses what you described by keeping operations smooth and cost-effective.";
        }
        break;

      case 'DISCOVERY':
        advice = "You probed for customer context effectively. Reinforce this by confirming stated requirements and constraints before transitioning to solutions.";
        if (cleanCustomer) {
          trySaying = "Based on what you shared about your requirements, let's look at the options specifically designed to meet those needs.";
        } else {
          trySaying = "Let me make sure I've captured all your core priorities and constraints before we move forward.";
        }
        break;

      case 'PRODUCT_KNOWLEDGE':
        advice = "You demonstrated strong subject-matter knowledge. Continue connecting each feature directly to the customer's stated daily benefits.";
        if (isSaaS) {
          trySaying = "Our platform centralizes incoming leads and automates task assignment so your entire team has real-time visibility across every stage.";
        } else {
          trySaying = "Along with these core capabilities, this solution directly addresses what you described by keeping operations smooth and cost-effective.";
        }
        break;

      case 'OBJECTION_HANDLING':
        advice = "You addressed customer hesitation with composure and credible reasoning. Maintain this confidence by contrasting lasting value against basic alternatives.";
        if (isSaaS) {
          trySaying = "I completely appreciate that concern—many growing teams start with the same hesitation, but once automated reminders take over manual tracking, conversion rates typically improve significantly.";
        } else {
          trySaying = "I completely understand that concern, and that is why this option includes comprehensive coverage to ensure reliable performance without unexpected costs.";
        }
        break;

      case 'CLOSING':
        if (isSaaS) {
          advice = "You guided the prospect toward a clear next step. Reinforce this by proposing a low-friction demo or trial tailored to their current workflow.";
          trySaying = "I would be happy to set up a quick 20-minute demo using your team's actual workflow so you can see the value firsthand before making a decision.";
        } else if (isBanking) {
          advice = "You guided the customer to a clear, actionable closing step. Reinforce this by confirming immediate authorization steps.";
          trySaying = "We can complete the verification right now through our secure system so your account setup is finalized today.";
        } else if (isRetail) {
          advice = "You guided the customer to a clear purchase conclusion. Reinforce this by assisting with checkout and delivery scheduling.";
          trySaying = "Let's head over to the counter to confirm your order and schedule delivery for your new appliance.";
        } else {
          advice = "You guided the conversation to a clear, actionable closing step. Reinforce this by confirming immediate timeline commitments and next steps.";
          trySaying = "Let's lock in these agreed next steps right now so you receive immediate confirmation and a clear schedule.";
        }
        break;

      case 'COMMUNICATION':
        advice = "You maintained excellent rapport and professional, attentive communication throughout. Continue concluding each exchange with proactive assistance.";
        if (isSaaS) {
          trySaying = "It was an absolute pleasure speaking with you today! I will prepare a concise summary of how this fits your workflow so you have all the facts.";
        } else if (isBanking) {
          trySaying = "It was an absolute pleasure assisting you today! I will ensure all your preferences are recorded and carried out smoothly.";
        } else if (isRetail) {
          trySaying = "It was an absolute pleasure assisting you today! Let me assist you through to the counter to make sure your order details are set up seamlessly.";
        } else {
          trySaying = "It was an absolute pleasure assisting you today! I will ensure all your requirements are handled smoothly.";
        }
        break;

      case 'EMPATHY':
        advice = "You validated the customer's situation with genuine empathy. Reinforce this by immediately linking your empathy to concrete priority handling.";
        if (custLower.includes('rent') || userLower.includes('rent')) {
          trySaying = "I completely understand that your rent is due today and this is critical, so I am prioritizing your transaction issue right now.";
        } else if (cleanCustomer) {
          trySaying = "I completely understand how critical this situation is for you, and I am giving your request my immediate personal attention.";
        } else {
          trySaying = "I completely understand how important this is to you, and I am giving your request my immediate personal attention to make sure it is handled right.";
        }
        break;

      case 'SECURITY':
        advice = "You maintained strict security compliance and protected customer privacy. Reinforce this by explicitly reassuring the customer about authorized procedures.";
        if (userLower.includes('otp') || userLower.includes('password') || custLower.includes('otp') || custLower.includes('password')) {
          trySaying = "For your security, please never share your OTP, password, or sensitive details, and we will complete this through our official portal.";
        } else {
          trySaying = "To keep your information strictly protected, we will complete all verification through our secure official channel.";
        }
        break;

      case 'ACCURACY':
        advice = "You communicated factual constraints clearly and set accurate expectations. Reinforce this by offering immediate verification steps.";
        trySaying = "To provide 100% accurate information, let me confirm the exact details from our system so you have complete clarity.";
        break;

      case 'OWNERSHIP':
        advice = "You demonstrated strong personal accountability. Reinforce this by giving the customer a definitive checkpoint time for their status update.";
        trySaying = "I am taking personal ownership of this request for you, and I will monitor it through completion and update you shortly.";
        break;

      case 'RESOLUTION':
        if (isTech) {
          advice = "You offered structured troubleshooting steps. Reinforce this by confirming each resolution step sequentially.";
          trySaying = "Here is our direct resolution plan: first, we will verify your access configuration, and second, confirm that your system is functioning normally.";
        } else if (isBanking) {
          advice = "You provided a clear resolution pathway. Reinforce this by tracking the transaction update until resolution.";
          trySaying = "Here is our direct resolution plan: I will initiate the transaction review immediately and confirm the status on your account.";
        } else {
          advice = "You offered constructive resolution steps. Strengthen your delivery by organizing the instructions into a clear sequence.";
          trySaying = "Here is our direct plan to resolve this: first, we will confirm the required adjustments, and second, ensure everything is in place for you.";
        }
        break;

      default:
        advice = "You communicated clearly and maintained a calm, professional demeanor. Reinforce this by concluding with a concise recap of agreed next actions.";
        trySaying = "To ensure we are completely aligned, I have logged your request and will follow up with you as soon as the next step is ready.";
        break;
    }
  } else {
    // ---------------- IMPROVEMENT / WEAKNESS ADDRESSING ----------------
    switch (compCat) {
      case 'VALUE_ALIGNMENT':
        if (evaluatorNote && evaluatorNote.length > 20 && !isGenericRecommendation(evaluatorNote)) {
          advice = evaluatorNote;
        } else {
          advice = isSaaS
            ? "Connect the software's features directly to the prospect's business impact, explaining how automated workflows prevent lost revenue and save team hours."
            : "Directly align the proposed solution with the customer's stated priorities, highlighting tangible benefits rather than just listing features.";
        }
        if (isSaaS) {
          trySaying = "By automating follow-up reminders and centralizing your customer records, your team saves multiple hours each week and avoids losing high-value enquiries.";
        } else if (isBanking) {
          trySaying = "Let me show you how this option maximizes your earnings while keeping your funds accessible whenever unexpected expenses arise.";
        } else if (isRetail) {
          trySaying = "Let me show you how this option saves energy on your monthly bills while providing the flexible storage your family needs.";
        } else {
          trySaying = "Let me connect this directly to what you're trying to achieve: this solution eliminates your main bottleneck and delivers dependable results.";
        }
        break;

      case 'DISCOVERY':
        if (evaluatorNote && evaluatorNote.length > 20 && !isGenericRecommendation(evaluatorNote)) {
          advice = evaluatorNote;
        } else {
          advice = "Ask focused discovery questions one at a time about the customer's specific needs, daily habits, and priority constraints before recommending a solution.";
        }
        if (isSaaS) {
          trySaying = "To help me recommend the right setup, could you share how your team currently tracks incoming enquiries and where delays typically happen?";
        } else if (custLower.includes('family') || custLower.includes('space') || custLower.includes('room') || custLower.includes('kitchen') || custLower.includes('size')) {
          trySaying = "To help you find the best fit, could you share a bit more about your space requirements and whether energy efficiency or capacity is your top priority?";
        } else if (cleanCustomer.length > 10) {
          trySaying = "To make sure I recommend the ideal option for you, could you tell me a little more about how you currently handle this and what specific challenges you're facing?";
        } else {
          trySaying = "Could you share your top two priorities and any specific constraints you'd like us to keep in mind?";
        }
        break;

      case 'PRODUCT_KNOWLEDGE':
        if (evaluatorNote && evaluatorNote.length > 20 && !isGenericRecommendation(evaluatorNote)) {
          advice = evaluatorNote;
        } else {
          advice = "Clearly explain technical features and specifications in accessible, benefit-focused language that directly connects to the customer's stated daily requirements.";
        }
        if (isSaaS) {
          trySaying = "Our CRM platform brings all your enquiries into one shared dashboard, assigning automatic follow-up reminders to your team members so no lead gets missed.";
        } else if (userLower.includes('convertible') || userLower.includes('freezer') || titleLower.includes('fridge') || titleLower.includes('refrigerator')) {
          trySaying = "This model features a convertible compartment, meaning you can easily switch that space between freezer and refrigerator modes depending on what food you need to store.";
        } else if (cleanCustomer.length > 10) {
          trySaying = "Let me explain how this feature works: it directly addresses what you described by saving you time and keeping your daily running costs low.";
        } else {
          trySaying = "Let me highlight how this solution's specific features deliver the exact performance and reliability you need.";
        }
        break;

      case 'OBJECTION_HANDLING':
        if (evaluatorNote && evaluatorNote.length > 20 && !isGenericRecommendation(evaluatorNote)) {
          advice = evaluatorNote;
        } else {
          advice = "Acknowledge pricing, competition, or timing concerns with value-anchored reasoning, emphasizing long-term benefits, reliability, and full support.";
        }
        if (isSaaS) {
          trySaying = "I completely understand evaluating subscription cost carefully; when you consider that recovering just one lost lead per month covers the software, the return on investment is immediate.";
        } else {
          trySaying = "I completely understand that price is an important consideration. When you look at the total efficiency, included warranty, and daily performance, it delivers substantial savings over time.";
        }
        break;

      case 'CLOSING':
        if (evaluatorNote && evaluatorNote.length > 20 && !isGenericRecommendation(evaluatorNote)) {
          advice = evaluatorNote;
        } else {
          advice = isSaaS
            ? "Propose a structured, low-risk next step—such as a tailored demo or pilot walkthrough—that allows the buyer to evaluate value without pressure."
            : "Summarize the agreed solution and guide the customer toward concrete, time-bound next steps with complete clarity.";
        }
        if (isSaaS) {
          trySaying = "Why don't we arrange a brief 20-minute walkthrough with your team's real workflow, so you can evaluate the time saved before making any commitment?";
        } else if (isRetail) {
          trySaying = "To confirm, we have selected the model that best fits your requirements. Let me guide you through finalizing the order and scheduling delivery.";
        } else if (isBanking) {
          trySaying = "Let me guide you through the secure steps to complete your authorization today and ensure your request is processed immediately.";
        } else {
          trySaying = "Let me walk you through our agreed next steps right now so we can move forward with complete clarity.";
        }
        break;

      case 'COMMUNICATION':
        if (evaluatorNote && evaluatorNote.length > 20 && !isGenericRecommendation(evaluatorNote)) {
          advice = evaluatorNote;
        } else {
          advice = "Demonstrate active listening by summarizing the customer's stated requirements and accompanying them proactively instead of offering passive dismissals.";
        }
        if (isSaaS) {
          trySaying = "I understand exactly what you need based on what you've shared about your follow-up bottlenecks. Let me walk you through how our platform streamlines that.";
        } else if (isRetail) {
          trySaying = "It was an absolute pleasure assisting you! Let me walk with you to make sure your order details are set up seamlessly.";
        } else {
          trySaying = "I understand exactly what you need based on what you've shared. Let me walk you through the simplest way we can get this done for you.";
        }
        break;

      case 'OWNERSHIP':
        advice = "Avoid deflecting responsibility or leaving the customer to navigate alone. Take personal accountability and specify a definitive checkpoint time when you will update them.";
        if (custLower.includes('rent') || userLower.includes('rent')) {
          trySaying = "I take personal accountability for resolving this rent issue. Let me handle the coordination right now, and I will follow up with you directly within 30 minutes.";
        } else {
          trySaying = "I take personal accountability for this issue. Let me handle the coordination right now, and I will follow up with you directly within 30 minutes.";
        }
        break;

      case 'EMPATHY':
        advice = "Acknowledge the customer's specific concern directly in your opening phrase rather than jumping straight into business procedures.";
        trySaying = "I hear how important this is to you, and I understand the frustration this has caused. Let me take care of this right away so you don't have to worry.";
        break;

      case 'SECURITY':
        advice = "Explicitly caution the customer against sharing sensitive credentials in unverified channels while directing them to secure authentication.";
        trySaying = "For your protection, please never share your OTP, PIN, or confidential password here. We will verify your account securely through the official authorized portal.";
        break;

      case 'ACCURACY':
        if (isRetail) {
          advice = "Provide clear, accurate specifications and delivery timelines directly from the system without guessing or using speculative jargon.";
          trySaying = "To give you exact clarity, let me confirm the specifications and delivery timeline directly from our system so you have 100% reliable information.";
        } else {
          advice = "Provide clear, accurate specifications and commitments directly from verified information without guessing or using speculative promises.";
          trySaying = "To give you exact clarity, let me confirm the official details directly so you have 100% reliable information.";
        }
        break;

      case 'RESOLUTION':
        if (isTech) {
          advice = evaluatorNote && !isGenericRecommendation(evaluatorNote) ? evaluatorNote : "Provide direct, step-by-step technical guidance that solves the user's issue immediately.";
          trySaying = "Here is what we will do right now to resolve this: let's reset your connection cache and verify your credentials on the secure server.";
        } else if (isBanking) {
          advice = evaluatorNote && !isGenericRecommendation(evaluatorNote) ? evaluatorNote : "Provide a definitive, step-by-step resolution plan and initiate the necessary account review immediately.";
          trySaying = "Here is what we can do right now to resolve this: I will initiate the verification on your account, lock in your priority status, and confirm your receipt.";
        } else {
          advice = evaluatorNote && !isGenericRecommendation(evaluatorNote) ? evaluatorNote : "Provide direct, step-by-step guidance that solves the customer's problem immediately rather than suggesting delayed or inconvenient channels.";
          trySaying = "Here is what we can do right now to resolve this: I will initiate the necessary updates in our system and confirm your status directly.";
        }
        break;

      default: {
        const compLabel = competencyName || 'this competency';
        advice = `Demonstrate strong proficiency in ${compLabel} by directly acknowledging the customer's requirements and offering tangible, reassuring guidance.`;
        trySaying = "I understand what you are looking for. Let me guide you through the exact steps to fulfill your request smoothly.";
        break;
      }
    }
  }

  const fullRecommendation = formatTrySayingFirst(trySaying, advice);

  return {
    advice,
    trySaying,
    fullRecommendation
  };
}
